// Client-side Web Push helpers.
// Flow: getVapidPublicKey() → enablePush() asks browser for permission,
// subscribes via the service worker, and POSTs {endpoint, keys} to the API.
// disablePush() unsubscribes locally and tells the server.
import { api } from "./chalkApi";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.ready;
}

// Convert URL-safe base64 → Uint8Array (required by pushManager.subscribe).
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = window.atob((b64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function enablePush() {
  if (!isPushSupported()) {
    const err = new Error("Push notifications aren't supported on this device.");
    err.code = "PUSH_UNSUPPORTED";
    throw err;
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    const err = new Error("Push permission denied.");
    err.code = "PERMISSION_DENIED";
    throw err;
  }
  // Get VAPID public key from server (single source of truth).
  const { public_key: vapid } = await api.pushVapidPublicKey();
  if (!vapid) {
    const err = new Error("Push isn't configured on the server.");
    err.code = "VAPID_NOT_CONFIGURED";
    throw err;
  }
  const reg = await getRegistration();
  // Reuse existing sub if present, else subscribe.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }
  // POST to API. Subscription.toJSON() yields {endpoint, keys: {p256dh, auth}}.
  const json = sub.toJSON();
  await api.pushSubscribe({
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent.slice(0, 400),
  });
  return sub;
}

export async function disablePush() {
  const sub = await getCurrentSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch { /* still try to tell the server */ }
  try {
    await api.pushUnsubscribe({ endpoint });
  } catch { /* server may already be out of sync */ }
  return { ok: true };
}
